import { Component, OnDestroy, ViewChild, TemplateRef, ViewContainerRef } from '@angular/core'
import { Feature, Polygon } from 'geojson'
import {
  BlacklistAreaProperties,
  DataBlacklistAreaService,
} from 'src/app/shared/services/data-blacklist-area.service'
import maplibregl, { MapGeoJSONFeature } from 'maplibre-gl'
import { MapService } from 'src/app/core/services/map.service'
import { MapSignsIcons } from 'src/app/core/enums/map-icons.enum'
import { DrawService } from '../../services/draw.service'
import { MapEventsService } from 'src/app/core/services/map-events.service'
import { debounceTime } from 'rxjs'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Layers } from 'src/app/shared/layers-config/layers'

const SPEED_SIGN_TYPES = new Set([
  MapSignsIcons.SPEED_LIMIT,
  MapSignsIcons.SPEED_LIMIT_END,
  MapSignsIcons.SPEED_LIMIT_ZONE,
  MapSignsIcons.SPEED_LIMIT_ZONE_END,
])

const COMMON_SPEEDS = [
  5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 90, 100, 110, 120, 130, 140,
]

@Component({
  selector: 'app-blacklist-area-dialog',
  templateUrl: './blacklist-area-dialog.component.html',
  styleUrls: ['./blacklist-area-dialog.component.scss'],
  standalone: false,
})
export class BlacklistAreaDialogComponent implements OnDestroy {
  @ViewChild('blacklistDialogTemplate', { static: true })
  blacklistDialogTemplate!: TemplateRef<any>

  public popup: any = null
  public drawnPolygon: Feature<Polygon> | null = null
  public editingAreaId: number | null = null

  public selectedSign: string = Object.values(MapSignsIcons)[0]
  public selectedSpeeds: number[] = []
  public customSpeedInput: number | null = null

  public readonly availableSigns = Object.values(MapSignsIcons)
  public readonly commonSpeeds = COMMON_SPEEDS

  public signSectionCollapsed = false
  public speedSectionCollapsed = false

  private originalSign: string = ''
  private originalSpeeds: number[] = []

  constructor(
    private blacklistAreaService: DataBlacklistAreaService,
    private mapService: MapService,
    private readonly mapEventService: MapEventsService,
    private viewContainerRef: ViewContainerRef,
    private drawService: DrawService,
  ) {
    this.mapEventService.featureClick
      .pipe(takeUntilDestroyed())
      .pipe(debounceTime(50))
      .subscribe((features: MapGeoJSONFeature[]) => {
        if (this.drawService.isDrawMode) return
        const hit = features[0]
        if (!hit || hit.layer.id !== Layers.BlacklistAreas.name) return
        const area = this.geoJsonFeatureToBlacklistAreaData(hit)
        this.showExistingBlacklistDialog(area, {
          lng: parseFloat(hit.state['selectedLng']),
          lat: parseFloat(hit.state['selectedLat']),
        })
      })
  }

  get isCurrentSignSpeedType(): boolean {
    return SPEED_SIGN_TYPES.has(this.selectedSign as MapSignsIcons)
  }

  private geoJsonFeatureToBlacklistAreaData(
    feature: MapGeoJSONFeature,
  ): Feature<Polygon, BlacklistAreaProperties> {
    const rawSpeeds = feature.properties?.['speeds']
    const speeds: number[] = Array.isArray(rawSpeeds)
      ? rawSpeeds.map(Number)
      : typeof rawSpeeds === 'string' && rawSpeeds.trim().startsWith('[')
        ? JSON.parse(rawSpeeds).map(Number)
        : []
    return {
      type: 'Feature',
      geometry: feature.geometry as Polygon,
      properties: {
        id: feature.properties?.['id'],
        mainSign: feature.properties?.['mainSign'],
        speeds,
      },
    }
  }

  isSpeedSignType(sign: string): boolean {
    return SPEED_SIGN_TYPES.has(sign as MapSignsIcons)
  }

  formatSignLabel(sign: string): string {
    return sign
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  }

  get speedSummaryLabel(): string {
    if (this.selectedSpeeds.length === 0) return 'All speeds'
    return [...this.selectedSpeeds]
      .sort((a, b) => a - b)
      .map((s) => `${s} km/h`)
      .join(', ')
  }

  onSignSelect(sign: string): void {
    this.selectedSign = sign
    if (!this.isSpeedSignType(sign)) {
      this.selectedSpeeds = []
    }
  }

  toggleSpeed(speed: number): void {
    if (this.selectedSpeeds.includes(speed)) {
      this.selectedSpeeds = this.selectedSpeeds.filter((s) => s !== speed)
    } else {
      this.selectedSpeeds = [...this.selectedSpeeds, speed].sort((a, b) => a - b)
    }
  }

  isSpeedSelected(speed: number): boolean {
    return this.selectedSpeeds.includes(speed)
  }

  addCustomSpeed(): void {
    const v = this.customSpeedInput
    if (v != null && v > 0 && !this.selectedSpeeds.includes(v)) {
      this.selectedSpeeds = [...this.selectedSpeeds, v].sort((a, b) => a - b)
      this.customSpeedInput = null
    }
  }

  removeSpeed(speed: number): void {
    this.selectedSpeeds = this.selectedSpeeds.filter((s) => s !== speed)
  }

  showBlacklistDialog(polygon: Feature<Polygon>, center: { lng: number; lat: number }): void {
    this.closeDialog()
    this.drawnPolygon = polygon
    this.selectedSign = Object.values(MapSignsIcons)[0]
    this.selectedSpeeds = []
    this.customSpeedInput = null
    this.editingAreaId = null
    this.openPopup(center)
  }

  showExistingBlacklistDialog(
    area: Feature<Polygon, BlacklistAreaProperties>,
    center: { lng: number; lat: number },
  ): void {
    this.closeDialog()
    this.drawnPolygon = area
    this.selectedSign = area.properties?.mainSign ?? ''
    this.selectedSpeeds = [...(area.properties?.speeds ?? [])]
    this.originalSign = area.properties?.mainSign ?? ''
    this.originalSpeeds = [...(area.properties?.speeds ?? [])]
    this.editingAreaId = area.properties?.id !== undefined ? area.properties.id : null
    this.openPopup(center)
  }

  isEditingExistingArea(): boolean {
    return this.editingAreaId !== null
  }

  hasChanges(): boolean {
    if (this.selectedSign !== this.originalSign) return true
    const sorted = [...this.selectedSpeeds].sort((a, b) => a - b)
    const origSorted = [...this.originalSpeeds].sort((a, b) => a - b)
    if (sorted.length !== origSorted.length) return true
    return sorted.some((s, i) => s !== origSorted[i])
  }

  async saveBlacklistArea(): Promise<void> {
    if (!this.drawnPolygon) return
    const speeds = this.isCurrentSignSpeedType ? this.selectedSpeeds : undefined
    if (this.editingAreaId) {
      await this.blacklistAreaService.updateBlacklistArea(
        this.drawnPolygon,
        this.editingAreaId,
        this.selectedSign,
        speeds,
      )
    } else {
      await this.blacklistAreaService.addBlacklistArea(this.drawnPolygon, this.selectedSign, speeds)
    }
    this.closeDialog()
  }

  async deleteBlacklistArea(): Promise<void> {
    if (!this.editingAreaId) return
    await this.blacklistAreaService.removeBlacklistArea(this.editingAreaId)
    this.editingAreaId = null
    this.closeDialog()
  }

  closeDialog(): void {
    if (this.popup) {
      this.popup.remove()
      this.popup = null
      if (!this.editingAreaId) {
        this.drawService.clearLastPolygon()
      }
    }
    this.drawnPolygon = null
    this.selectedSign = Object.values(MapSignsIcons)[0]
    this.selectedSpeeds = []
    this.customSpeedInput = null
    this.editingAreaId = null
    this.originalSign = ''
    this.originalSpeeds = []
  }

  private openPopup(center: { lng: number; lat: number }): void {
    const viewRef = this.viewContainerRef.createEmbeddedView(this.blacklistDialogTemplate)
    viewRef.detectChanges()
    const popupContent = viewRef.rootNodes[0]

    this.popup = new maplibregl.Popup({
      offset: 5,
      closeButton: false,
      closeOnClick: false,
    })
      .setLngLat([center.lng, center.lat])
      .setDOMContent(popupContent)
      .addTo(this.mapService.map)

    this.popup.on('close', () => {
      this.popup = null
    })
  }

  ngOnDestroy(): void {
    if (this.popup) {
      this.popup.remove()
    }
  }
}
